import { useState } from 'react';
import './HorizontalProductListing.css';
import { ApiProduct, ApiSpecialsProduct } from '../../types';
import { Link } from 'react-router-dom';
import { QuantityInput } from '../Form/QuantityInput';
import { useCart } from '../../state/cart';
import { useFormatting, useTranslation } from '../../i18n/i18n';
import { Img } from '../Img/Img';
import { Button } from '../Form/Button';
import { FlyToCartContainer } from '../FlyToCart/FlyToCart';
import { DeliveryTime } from '../product/DeliveryTime';
import { useConfig } from '../../config';
import { useAnalytics } from '../../hooks/useAnalytics';
import { PriceTagSimple } from '../../components/PriceTagSimple/PriceTagSimple';
import { useCriteo } from '../../hooks/useCriteo';
import { ProductContainer } from '../../state/product';
import { useLoop54Events } from '../../hooks/useLoop54Events';
import { UiContainer } from '../../state/ui';
import { MetaContainer } from '../../state/meta';
import { calculateVatMultiplier } from '../../utils/vatUtils';

type HorizontalProductListingProps = {
	products: ApiProduct[];
	highlightId?: number;
	supercategoryId?: number;
};
export const HorizontalProductListing = ({
	products,
	highlightId,
	supercategoryId,
}: HorizontalProductListingProps): JSX.Element => {
	const { getSpecialsProducts } = ProductContainer.useContainer();
	const isSpecialProduct = (product: ApiProduct | ApiSpecialsProduct): product is ApiSpecialsProduct => {
		return 'newPrice' in product;
	};

	const mixedSpecialProducts = getSpecialsProducts(products);
	return (
		<div className="product-wrapper">
			{mixedSpecialProducts.map((p) => {
				const isSpecialPriceProduct = isSpecialProduct(p);
				return (
					<ProductRow
						highlight={isSpecialPriceProduct ? p.product.id === highlightId : p.id === highlightId}
						product={isSpecialPriceProduct ? p.product : p}
						key={isSpecialPriceProduct ? p.productId : p.id}
						supercategoryId={supercategoryId}
					/>
				);
			})}
		</div>
	);
};

const ProductRow = ({
	product,
	highlight,
	supercategoryId,
}: {
	product: ApiProduct;
	highlight?: boolean;
	supercategoryId?: number;
}): JSX.Element => {
	const { setQuantity, currentCount } = useCart();
	const [quantityField, setQuantityField] = useState(product.bundleSize);
	const { flyToCart } = FlyToCartContainer.useContainer();
	const t = useTranslation().product;
	const { currencyCode } = useConfig();
	const { sendEvent, sendTorkEvent } = useAnalytics();
	const { sendCriteoEvent } = useCriteo();
	const { getFinalPrice, specialPricesIndex } = ProductContainer.useContainer();
	const { store } = useConfig();
	const { sendLoop54Event } = useLoop54Events();
	const disabled = product.deliveryClass === 6;
	const { marketing } = MetaContainer.useContainer().trackingConsents;

	const handleAddToCart = (id: number) => {
		setQuantity(id, currentCount(id) + quantityField);
		flyToCart();
		sendEvent({
			event: 'add_to_cart',
			ecommerce: {
				value: product.price,
				currency: currencyCode,
				items: [
					{
						item_id: String(id),
						item_name: product.name,
						price: product.price,
						quantity: quantityField,
					},
				],
			},
		});

		if (store === 'kgno' && product.name.toLowerCase().includes('tork')) {
			sendTorkEvent('AddToCart', {
				revenue: product.price * quantityField,
			});
		}
		sendLoop54Event({ events: [{ type: 'addtocart', entity: { type: 'Product', id: product.id + '' } }] });

		sendCriteoEvent({
			event: 'addToCart',
			item: [
				{
					id: product.id + '',
					price: product.price,
					quantity: quantityField,
				},
			],
		});

		if (typeof window.fbq !== 'undefined') {
			if (store === 'kgno') {
				if (product.name.toLowerCase().includes('tork') && marketing) {
					window.fbq('track', 'AddToCart', {
						value: product.price * quantityField,
						currency: 'NOK',
					});
				}
			} else {
				window.fbq('track', 'AddToCart', {
					content_type: 'product',
					content_ids: [id + ''],
					content_name: product.name,
					value: quantityField * price,
					currency: currencyCode,
				});
			}
		}
	};

	const {
		id,
		displayId,
		name,
		image,
		perma,
		bundleSize,
		price,
		campaignPrice,
		deliveryClass,
		deliveryNotice,
		vatRate,
	} = product;
	const productNotInStock = deliveryClass === 3 || deliveryClass === 4;
	const specialPriceProduct = specialPricesIndex ? specialPricesIndex[product.id] : undefined;

	return (
		<div className="row">
			<div
				className={`product-image ${highlight ? `category-color-${supercategoryId}-light` : ''} ${
					highlight && productNotInStock ? 'product-not-in-stock-color-light' : ''
				}`}
			>
				<Link to={'/' + perma}>
					{productBadge(specialPriceProduct, 'only-mobile')}
					{/*
					 * The highlighted row - the product being looked at, among its alternatives - sits
					 * on the category colour, so its picture asks for the cut-out version the way the
					 * highlighted box in a listing does. Products without one are served the ordinary
					 * picture under the same name, so nothing breaks where there is no cut-out.
					 */}
					<Img
						src={image}
						width={50}
						height={50}
						imgWidth={50}
						imgHeight={50}
						alt={name}
						transparent={highlight}
					/>
				</Link>
			</div>
			<div
				className={`product-details-wrapper ${highlight ? `category-color-${supercategoryId}-light` : ''} ${
					highlight && productNotInStock ? 'product-not-in-stock-color-light' : ''
				}`}
			>
				<div className="product-link">
					<Link to={'/' + perma}>{name}</Link>
				</div>
				<div className="article-number not-mobile">
					{t.articleNumber} {displayId}
				</div>
				<div className="delivery-time">
					<DeliveryTime
						deliveryClass={deliveryClass}
						deliveryNotice={deliveryNotice}
						textLength={'short-one-line'}
					/>
				</div>
				<div className="price">
					<div className="price-wrapper">
						<PriceTagSimple
							price={getFinalPrice(id, price, 1, campaignPrice)}
							vatRate={vatRate}
							isCampaign={campaignPrice > 0 || !!specialPriceProduct}
						></PriceTagSimple>
						{productBadge(specialPriceProduct, 'not-mobile')}
					</div>
				</div>
				<div className="not-mobile">
					<QuantityInput
						defaultValue={quantityField}
						onBlur={(value) => setQuantityField(value)}
						step={bundleSize}
					/>
				</div>
				<div className="buy-button not-mobile">
					<Button
						className={`buy-button ${highlight && productNotInStock ? 'product-not-in-stock-color' : ''} ${disabled ? 'disabled' : ''} plausible-event-name=Add+to+Cart`}
						onClick={() => handleAddToCart(id)}
						disabled={disabled}
					>
						{t.buyNow}
					</Button>
				</div>
			</div>
		</div>
	);
};

function productBadge(specialPriceProduct: ApiSpecialsProduct | undefined, className?: string): JSX.Element | null {
	const { asPrice } = useFormatting();
	const ui = UiContainer.useContainer();
	if (specialPriceProduct) {
		const { vatRate } = specialPriceProduct.product;
		const vatScale = calculateVatMultiplier(vatRate, ui.priceWithVat);
		const priceDiff = asPrice(
			specialPriceProduct.product.price * vatScale - specialPriceProduct.newPrice * vatScale
		);
		return (
			<div className={`price-badge ${className ? className : ''}`}>
				<div className="ribbon special-price">-{priceDiff}</div>
			</div>
		);
	}
	return null;
}
